import React, { useState } from "react"
import { useMutation } from "urql"

import { Button } from "../../components/shared/Buttons"
import CodeEditor from "../../components/shared/CodeEditor"
import { Banner } from "../../components/shared/Layout"
import Typography from "../../components/shared/Typography"
import { useToast } from "../../contexts/ToastContext"
import { UPDATE_PRACTICE_SCRIPTS_MUTATION } from "../../graphql/mutations/updatePracticeScripts"

const CustomScriptsSettings = ({ practice }) => {
  const [scripts, setScripts] = useState({
    headScripts: practice.headScripts || "",
    bodyScripts: practice.bodyScripts || ""
  })
  const [lastSavedValues, setLastSavedValues] = useState({
    headScripts: practice.headScripts || "",
    bodyScripts: practice.bodyScripts || ""
  })

  const [{ fetching }, updateScripts] = useMutation(UPDATE_PRACTICE_SCRIPTS_MUTATION)
  const { showToast } = useToast()

  const handleHeadScriptChange = (newCode) => {
    setScripts((prev) => ({ ...prev, headScripts: newCode }))
  }

  const handleBodyScriptChange = (newCode) => {
    setScripts((prev) => ({ ...prev, bodyScripts: newCode }))
  }

  const handleSaveHeadScript = () => {
    updateScripts({ headScripts: scripts.headScripts }).then((result) => {
      if (result?.data?.updatePracticeScripts?.result === "success") {
        setLastSavedValues((prev) => ({ ...prev, headScripts: scripts.headScripts }))
        showToast("Head scripts updated successfully")
      } else {
        const errorMessage = result?.data?.updatePracticeScripts?.errors?.join(" ") || "An error occurred"
        showToast({
          content: errorMessage,
          type: "error"
        })
      }
    })
  }

  const handleSaveBodyScript = () => {
    updateScripts({ bodyScripts: scripts.bodyScripts }).then((result) => {
      if (result?.data?.updatePracticeScripts?.result === "success") {
        setLastSavedValues((prev) => ({ ...prev, bodyScripts: scripts.bodyScripts }))
        showToast("Body scripts updated successfully")
      } else {
        const errorMessage = result?.data?.updatePracticeScripts?.errors?.join(" ") || "An error occurred"
        showToast({
          content: errorMessage,
          type: "error"
        })
      }
    })
  }

  return (
    <>
      <Typography as="h2" variant="h4" className="mb-4">
        Custom Scripts
      </Typography>

      <Banner type="warning" className="mb-6">
        <p>Note: Scripts added here will only run on your public website, not in this admin interface.</p>
        <p className="mt-1">After saving, preview your website to see the scripts in action.</p>
      </Banner>

      <div className="flex w-full flex-col gap-8">
        <div className="w-full rounded-xl border border-gray-light p-6">
          <Typography as="h3" variant="h5" className="mb-2">
            Head Scripts
          </Typography>

          <Typography as="p" className="mb-3 text-gray-dark">
            Add custom scripts to be included in the{" "}
            <code className="bg-gray-ultralight px-1 font-mono">&lt;head&gt;</code> section of your website. Be careful
            with what you add here as it could affect your website&apos;s functionality.
          </Typography>

          <div className="mb-4">
            <CodeEditor
              value={scripts.headScripts}
              onChange={handleHeadScriptChange}
              language="html"
              height="250px"
              className="w-full"
              placeholder="<!-- Add scripts for the head section here -->"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSaveHeadScript}
              disabled={fetching || scripts.headScripts === lastSavedValues.headScripts}>
              {fetching ? "Saving..." : "Save Head Scripts"}
            </Button>
          </div>
        </div>

        <div className="w-full rounded-xl border border-gray-light p-6">
          <Typography as="h3" variant="h5" className="mb-2">
            Body Scripts
          </Typography>

          <Typography as="p" className="mb-3 text-gray-dark">
            Add custom scripts to be included before the closing{" "}
            <code className="bg-gray-ultralight px-1 font-mono">&lt;/body&gt;</code> tag. This is ideal for analytics,
            tracking codes, and other scripts that should load after your page content.
          </Typography>

          <div className="mb-4">
            <CodeEditor
              value={scripts.bodyScripts}
              onChange={handleBodyScriptChange}
              language="html"
              height="250px"
              className="w-full"
              placeholder="<!-- Add scripts for the body section here -->"
            />
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSaveBodyScript}
              disabled={fetching || scripts.bodyScripts === lastSavedValues.bodyScripts}>
              {fetching ? "Saving..." : "Save Body Scripts"}
            </Button>
          </div>
        </div>

        <div className="mt-2">
          <Typography as="h3" variant="h5" className="mb-2">
            Security Tips
          </Typography>
          <ul className="ml-5 list-disc text-gray-dark">
            <li>Only add scripts from trusted sources</li>
            <li>Be careful with tracking scripts as they may affect user privacy</li>
            <li>Test your website after adding new scripts to ensure everything works properly</li>
            <li>Use syntax highlighting to check for script errors</li>
          </ul>
        </div>
      </div>
    </>
  )
}

export default CustomScriptsSettings
